import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiToast } from '@hooks/useApiToast';
import { enterpriseApiClient } from '../../client';
import { WorkOrdersApi } from '../../openapi';
import type {
  CreateWorkOrderChargeCommand,
  WorkOrderChargeViewModel
} from '../../openapi';
import { workOrderKeys } from '../constants';
import { db } from '@db/database';
import { enqueue, processAll } from '@services/offlineSyncService';
import { useUserStore } from '@stores/userStore';

type AddWorkOrderChargeParams = {
  jobNumber: string;
  chargeData: CreateWorkOrderChargeCommand;
};

type MutationContext = {
  previousCharges: WorkOrderChargeViewModel[] | undefined;
};

type UseAddWorkOrderChargeOptions = {
  suppressToast?: boolean;
};

export const useAddWorkOrderCharge = (
  onSuccessCallback?: () => void,
  options?: UseAddWorkOrderChargeOptions
) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();
  const workOrdersApi = new WorkOrdersApi(
    undefined,
    undefined,
    enterpriseApiClient
  );

  return useMutation<
    WorkOrderChargeViewModel,
    Error,
    AddWorkOrderChargeParams,
    MutationContext
  >({
    networkMode: 'always',
    mutationFn: async ({ jobNumber, chargeData }) => {
      if (!navigator.onLine) {
        const tempSeq = -Date.now();
        const userId = useUserStore.getState().userName;

        await db.cachedCharges.add({
          ...chargeData,
          jobNumber,
          sequenceNumber: tempSeq,
          cachedAt: new Date().toISOString(),
          _userId: userId
        } as never);

        await enqueue({
          entityType: 'charge_add',
          entityKey: `${jobNumber}:${tempSeq}`,
          payload: { jobNumber, chargeData },
          priority: 50
        });

        return {
          ...chargeData,
          jobNumber,
          sequenceNumber: tempSeq
        } as unknown as WorkOrderChargeViewModel;
      }

      const response = await workOrdersApi.apiWorkOrdersJobNumberChargesPost({
        jobNumber,
        createWorkOrderChargeCommand: chargeData
      });
      return response.data;
    },
    onMutate: async ({ jobNumber, chargeData }) => {
      await queryClient.cancelQueries({
        queryKey: workOrderKeys.charges(jobNumber)
      });

      const previousCharges = queryClient.getQueryData<
        WorkOrderChargeViewModel[]
      >(workOrderKeys.charges(jobNumber));

      queryClient.setQueryData<WorkOrderChargeViewModel[]>(
        workOrderKeys.charges(jobNumber),
        old => {
          if (!old) return old;
          const optimisticCharge: WorkOrderChargeViewModel = {
            ...chargeData,
            sequenceNumber: -Date.now(),
            jobNumber
          } as WorkOrderChargeViewModel;
          return [...old, optimisticCharge];
        }
      );

      return { previousCharges };
    },
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.charges(variables.jobNumber)
      });
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.byJobNumber(variables.jobNumber)
      });
      if (!options?.suppressToast) {
        showSuccess('addChargeModal.success', 'charges');
      }
      onSuccessCallback?.();

      if (!navigator.onLine) {
        processAll();
      }

      if (navigator.onLine) {
        try {
          const metadata = await db.downloadMetadata.get(
            String(variables.jobNumber)
          );
          if (metadata?.status === 'cached') {
            const userId = useUserStore.getState().userName;
            await db.cachedCharges.add({
              ..._data,
              jobNumber: String(variables.jobNumber),
              cachedAt: new Date().toISOString(),
              _userId: userId
            } as never);
          }
        } catch {
          // Best effort — online React Query cache is still fresh
        }
      }
    },
    onError: (error, variables, context) => {
      if (context?.previousCharges) {
        queryClient.setQueryData(
          workOrderKeys.charges(variables.jobNumber),
          context.previousCharges
        );
      }
      showError('addChargeModal.error', error, 'charges');
    }
  });
};
